import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../styles/ViewSwitcher.css';

/*
 * "Lens" view switcher (2026-10-27, mockup https://claude.ai/artifact/2v346GJRz1BuwQBFXTjdgK, option C).
 * The selected view shows icon + name in a sliding pill; the others are icons only. Hovering glides a
 * soft lens to the pointed icon and shows its name in a tooltip. Nothing changes width while hovering —
 * only a click does — so the icons never move under the pointer.
 *
 * views: [{ id, label, icon }]   value: selected id   onChange(id)
 */
export default function ViewSwitcher({ views, value, onChange, className = '', ariaLabel = 'View' }) {
  const barRef = useRef(null);
  const pillRef = useRef(null);
  const lensRef = useRef(null);
  const btnRefs = useRef([]);
  const hoverRef = useRef(-1);
  const [hover, setHover] = useState(-1);
  const [ready, setReady] = useState(false);
  const activeIdx = Math.max(0, views.findIndex((v) => v.id === value));

  const place = (el, i) => {
    const b = btnRefs.current[i];
    if (!el || !b) return;
    el.style.left = `${b.offsetLeft}px`;
    el.style.width = `${b.offsetWidth}px`;
  };

  // The selected button's name opens with a width transition; follow it so the pill lands on the final size.
  useLayoutEffect(() => {
    let raf;
    const t0 = performance.now();
    const follow = () => {
      place(pillRef.current, activeIdx);
      if (hoverRef.current >= 0) place(lensRef.current, hoverRef.current);
      if (performance.now() - t0 < 400) raf = requestAnimationFrame(follow);
    };
    follow();
    if (!ready) raf = requestAnimationFrame(() => setReady(true)); // no slide-in on first paint
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdx]);

  // Late font loads change widths — re-place on resize.
  useLayoutEffect(() => {
    if (!barRef.current || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      place(pillRef.current, activeIdx);
      if (hoverRef.current >= 0) place(lensRef.current, hoverRef.current);
    });
    ro.observe(barRef.current);
    return () => ro.disconnect();
  }, [activeIdx]);

  // The toolbar clips its overflow, so the hovered view's name is drawn in a portal at fixed coordinates.
  const [tip, setTip] = useState(null);

  const pointAt = (i) => {
    hoverRef.current = i;
    setHover(i);
    if (i >= 0) place(lensRef.current, i);
    const b = btnRefs.current[i];
    if (i >= 0 && i !== activeIdx && b) {
      const r = b.getBoundingClientRect();
      setTip({ label: views[i].label, x: r.left + r.width / 2, y: r.bottom + 7 });
    } else {
      setTip(null);
    }
  };

  const onKeyDown = (e) => {
    let j = -1;
    if (e.key === 'ArrowRight') j = Math.min(views.length - 1, activeIdx + 1);
    if (e.key === 'ArrowLeft') j = Math.max(0, activeIdx - 1);
    if (j < 0) return;
    e.preventDefault();
    onChange(views[j].id);
    btnRefs.current[j]?.focus();
  };

  return (
    <div
      ref={barRef}
      className={`vsw${hover >= 0 ? ' vsw--hot' : ''}${ready ? ' vsw--ready' : ''} ${className}`.trim()}
      role="tablist"
      aria-label={ariaLabel}
      onMouseLeave={() => pointAt(-1)}
    >
      <span ref={pillRef} className="vsw-pill" aria-hidden="true" />
      <span ref={lensRef} className="vsw-lens" aria-hidden="true" />
      {views.map((v, i) => {
        const active = i === activeIdx;
        return (
          <button
            key={v.id}
            ref={(el) => { btnRefs.current[i] = el; }}
            type="button"
            role="tab"
            aria-selected={active}
            aria-label={v.label}
            tabIndex={active ? 0 : -1}
            className={`vsw-btn${active ? ' is-active' : ''}${hover === i ? ' is-hover' : ''}`}
            onClick={() => { setTip(null); onChange(v.id); }}
            onMouseEnter={() => pointAt(i)}
            onFocus={() => pointAt(i)}
            onBlur={() => pointAt(-1)}
            onKeyDown={onKeyDown}
          >
            <span className="vsw-icon">{v.icon}</span>
            <span className="vsw-label">{v.label}</span>
          </button>
        );
      })}
      {tip && createPortal(
        <span key={tip.label} className="vsw-tip" style={{ left: tip.x, top: tip.y }} aria-hidden="true">{tip.label}</span>,
        document.body,
      )}
    </div>
  );
}
